// Lib
import { isEmpty } from 'lodash/fp';

// Utils
import randomString from '../../../../common/uid/randomString';
import { asRect } from '../../../../common/maths/geometry/rect';
import { asObject } from '../../../../common/utils/immutableHelper';
import { canvasElementSorter } from '../../../../common/elements/utils/elementSortUtils';
import { getBoundingRectInGridUnits } from '../../../canvas/dnd/canvasUtil';
import { isCommentThread } from '../../../../common/elements/utils/elementTypeUtils';
import getElementClipboardHtmlString from '../utils/getElementClipboardHtmlString';

// Constants
import { SaveToClipboardArgs } from './clipboardActions';
import { ClipboardOperation, DATA_TRANSFER_TYPES } from './clipboardConstants';
import { ImMNElement, MNElement } from '../../../../common/elements/elementModelTypes';
import { ImElementMeasurementMap } from '../../../components/measurementsStore/elementMeasurements/elementMeasurementsTypes';
import { Rect } from '../../../../common/maths/geometry/rect/rectTypes';

type AddElementHtmlDataToClipboardProps = {
    event: ClipboardEvent;
    elements: MNElement[];
};

const addElementHtmlDataToClipboard = ({ event, elements }: AddElementHtmlDataToClipboardProps) => {
    if (isEmpty(elements)) return;

    let htmlStr = '';

    // Loop through each element and get its HTML representation to add to the clipboard
    for (const element of elements) {
        const elementHtmlStr = getElementClipboardHtmlString(element);

        if (!elementHtmlStr) continue;

        // Separate each element with an empty div
        htmlStr = `${htmlStr}${elementHtmlStr}<div></div>`;
    }

    if (isEmpty(htmlStr)) return;

    htmlStr = `<meta charSet='utf-8'>${htmlStr}`;

    event.clipboardData?.setData(DATA_TRANSFER_TYPES.HTML, htmlStr);
};

type AddElementDataToClipboardProps = {
    event: ClipboardEvent;
    saveId: string;
    operation: ClipboardOperation;
    sessionId: string;
    elements: MNElement[];
    elementsBoundingRect: Rect;
};

const addElementDataToClipboard = ({
    event,
    saveId,
    operation,
    sessionId,
    elements,
    elementsBoundingRect,
}: AddElementDataToClipboardProps) => {
    // Our application "elements" data type
    const dataString = JSON.stringify({ saveId, operation, sessionId, elements, elementsBoundingRect });
    event.clipboardData?.setData(DATA_TRANSFER_TYPES.ELEMENTS, dataString);

    // Save as HTML for other programs
    addElementHtmlDataToClipboard({ event, elements });
};

type HandleSaveToClipboardEventProps = {
    dispatchGetMeasurementsMap: () => ImElementMeasurementMap;
    dispatchGetSelectedElements: () => Immutable.List<ImMNElement>;
    dispatchGetCurrentlyEditingId: () => string;
    dispatchGetCurrentModalElementId: () => string;
    dispatchSaveToClipBoard: (args: SaveToClipboardArgs) => void;
    gridSize: number;
    dispatchGetPageId: () => string;
};

// TODO - This looks like it should be a thunk action handler...
export const handleSaveToClipboardEvent = (
    event: ClipboardEvent,
    operation: ClipboardOperation,
    props: HandleSaveToClipboardEventProps,
) => {
    const {
        dispatchGetMeasurementsMap,
        dispatchGetSelectedElements,
        dispatchGetCurrentlyEditingId,
        dispatchGetCurrentModalElementId,
    } = props;

    const measurements = dispatchGetMeasurementsMap();

    const selectedElements = dispatchGetSelectedElements();
    const currentlyEditingId = dispatchGetCurrentlyEditingId();
    const currentModalElementId = dispatchGetCurrentModalElementId();

    if (!selectedElements.size || currentlyEditingId || currentModalElementId) return;

    // If only selecting a comment, then let the user highlight and select comment text
    if (selectedElements.size === 1 && isCommentThread(selectedElements.first())) return;

    event.preventDefault();

    const { dispatchSaveToClipBoard, gridSize, dispatchGetPageId } = props;

    const saveId = randomString(16);
    const sessionId = dispatchGetPageId();
    const elements = asObject(selectedElements.sort(canvasElementSorter)) as MNElement[];
    const elementsBoundingRect = asRect(
        getBoundingRectInGridUnits({
            elements,
            measurements,
            gridSize,
        }),
    );

    addElementDataToClipboard({ event, saveId, elements, elementsBoundingRect, operation, sessionId });

    // On paste start offset at original location (index of -1)
    const pasteCount = operation === ClipboardOperation.cut ? -1 : 0;
    dispatchSaveToClipBoard({ operation, elements, saveId, pasteCount, elementsBoundingRect });
};
